import React from 'react';
import PropTypes from 'prop-types';

export default function PressureDiffLineGraph({
    width = 600,
    height = 400,
    id = '',
    labels,
    seriesA,
    seriesB,
    xAxisLabel = '',
    yAxisLabel = '',
    seriesAName = 'Series A',
    seriesBName = 'Series B',
    minYAxis = 0,
    maxYAxis = 250,
    minXAxis = 0,
    maxXAxis = 12,
}) {
    const chartId = id ? `svg-chart-${id}` : `svg-chart-${Math.random().toString(36).substr(2, 9)}`;
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Use fixed axis ranges instead of calculating from data
    const minY = minYAxis;
    const maxY = maxYAxis;

    // Calculate y-axis ticks (every 25 units)
    const yTickInterval = 25;
    const yTickValues = [];
    for (let i = minY; i <= maxY; i += yTickInterval) {
        yTickValues.push(i);
    }
    yTickValues.reverse(); // Top to bottom for rendering

    // Calculate x-axis ticks based on axis range
    const xTickValues = [];
    for (let i = minXAxis; i <= maxXAxis; i++) {
        xTickValues.push(i);
    }

    // Helper to map series to SVG points with fixed axis ranges
    const mapPoints = (series) =>
        series
            .map((value, i) => {
                if (value === null || value === undefined || value === '') return null;
                // Map x position based on actual label value relative to x-axis range
                const labelValue = labels[i];
                const xRatio = (labelValue - minXAxis) / (maxXAxis - minXAxis);
                const x = padding + xRatio * chartWidth;
                // Clamp y values to axis range
                const clampedValue = Math.max(minY, Math.min(maxY, value));
                const y = padding + chartHeight - ((clampedValue - minY) / (maxY - minY)) * chartHeight;
                return [x, y];
            })
            .filter(Boolean); // Remove nulls

    const pointsA = seriesA ? mapPoints(seriesA) : [];
    const pointsB = seriesB ? mapPoints(seriesB) : [];

    const polylinePointsA = pointsA.map(([x, y]) => `${x},${y}`).join(' ');
    const polylinePointsB = pointsB.map(([x, y]) => `${x},${y}`).join(' ');

    return (
        <svg id={chartId} width={width} height={height}>
            {/* Axes */}
            <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#333" />
            <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#333" />

            {/* X-axis label */}
            {xAxisLabel && (
                <text x={width / 2} y={height - 5} textAnchor="middle" fontSize="14" fontWeight="bold">
                    {xAxisLabel}
                </text>
            )}

            {/* Y-axis label (rotated) */}
            {yAxisLabel && (
                <text
                    x={padding - 25}
                    y={padding + chartHeight * 0.6} // 60% down the chart area
                    textAnchor="middle"
                    fontSize="14"
                    fontWeight="bold"
                    transform={`rotate(-90 ${padding - 25},${padding + chartHeight * 0.6})`}>
                    {yAxisLabel}
                </text>
            )}

            {/* Labels */}
            {xTickValues.map((tickValue) => {
                const xRatio = (tickValue - minXAxis) / (maxXAxis - minXAxis);
                const x = padding + xRatio * chartWidth;
                return (
                    <text key={tickValue} x={x} y={height - padding + 20} textAnchor="middle" fontSize="12">
                        {tickValue}
                    </text>
                );
            })}

            {/* Y-axis ticks and grid lines */}
            {yTickValues.map((value, i) => {
                const y = padding + ((maxY - value) / (maxY - minY || 1)) * chartHeight;
                return (
                    <g key={i}>
                        {/* Dim grey grid line */}
                        <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#bbb" strokeDasharray="4 2" />
                        {/* Y-axis label */}
                        <text
                            x={padding - 10}
                            y={y}
                            textAnchor="end"
                            fontSize="12"
                            alignmentBaseline="middle"
                            fill="#555">
                            {Math.round(value)}
                        </text>
                    </g>
                );
            })}

            {/* Data line A */}
            {seriesA && (
                <>
                    <polyline fill="none" stroke="#1976d2" strokeWidth="2" points={polylinePointsA} />
                    {pointsA.map(([x, y], i) => (
                        <circle key={`a-${i}`} cx={x} cy={y} r={3} fill="#1976d2" />
                    ))}
                </>
            )}

            {/* Data line B */}
            {seriesB && (
                <>
                    <polyline fill="none" stroke="#d32f2f" strokeWidth="2" points={polylinePointsB} />
                    {pointsB.map(([x, y], i) => (
                        <circle key={`b-${i}`} cx={x} cy={y} r={3} fill="#d32f2f" />
                    ))}
                </>
            )}

            {/* Legend above the graph */}
            {seriesA && (
                <>
                    {/* Legend for seriesA */}
                    <rect x={padding} y={padding - 30} width={18} height={6} fill="#1976d2" />
                    <text x={padding + 25} y={padding - 24} fontSize="12" alignmentBaseline="middle">
                        {seriesAName}
                    </text>
                </>
            )}
            {seriesB && (
                <>
                    {/* Legend for seriesB */}
                    <rect x={padding + 70} y={padding - 30} width={18} height={6} fill="#d32f2f" />
                    <text x={padding + 95} y={padding - 24} fontSize="12" alignmentBaseline="middle">
                        {seriesBName}
                    </text>
                </>
            )}
        </svg>
    );
}

PressureDiffLineGraph.propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    id: PropTypes.string,
    labels: PropTypes.arrayOf(PropTypes.number).isRequired,
    seriesA: PropTypes.arrayOf(PropTypes.number),
    seriesB: PropTypes.arrayOf(PropTypes.number),
    xAxisLabel: PropTypes.string,
    yAxisLabel: PropTypes.string,
    seriesAName: PropTypes.string,
    seriesBName: PropTypes.string,
    minYAxis: PropTypes.number,
    maxYAxis: PropTypes.number,
    minXAxis: PropTypes.number,
    maxXAxis: PropTypes.number,
};
